import type { Quality } from './RenderContext';

/**
 * Automatic graphics quality (the "Auto" setting). The first guess comes from
 * the GPU's name and the device's cores and memory; after that the frame rate
 * decides. When the median frame stays slower than 30 fps once dynamic
 * resolution has done what it can, quality drops one level. After a long
 * smooth stretch it tries one level up, and a level that proved too slow
 * becomes the ceiling for this device, so it never oscillates.
 */

const ORDER: readonly Quality[] = ['low', 'medium', 'high'];
const rank = (q: Quality): number => ORDER.indexOf(q);

export function lowerQuality(q: Quality): Quality {
  return ORDER[Math.max(0, rank(q) - 1)] ?? 'low';
}

export function higherQuality(q: Quality): Quality {
  return ORDER[Math.min(ORDER.length - 1, rank(q) + 1)] ?? 'high';
}

export interface DeviceInfo {
  /** WebGL renderer, e.g. "ANGLE (Intel, Intel(R) UHD Graphics 620 Direct3D11 vs_5_0 ps_5_0)". */
  gpu: string;
  /** Logical processors (`navigator.hardwareConcurrency`). */
  cores: number;
  /** `navigator.deviceMemory` in GB (Chromium only, capped at 8); null when unknown. */
  memoryGb: number | null;
  /** The primary pointer is a finger (phones and tablets). */
  mobile: boolean;
}

const SOFTWARE_GPU = /swiftshader|llvmpipe|softpipe|lavapipe|software|basic render/i;
const DISCRETE_GPU = /nvidia|geforce|quadro|\brtx\b|\bgtx\b|radeon\s*(rx|pro|r9|vii)|\barc(\(tm\))?\s*a\d|apple m\d/i;
const INTEGRATED_GPU = /intel|iris|\buhd\b|\bhd graphics|adreno|mali|powervr|apple gpu|radeon(\(tm\))?\s*(graphics|vega)/i;

/** WebGL drawn by the CPU (hardware acceleration off or unavailable): only Low is playable. */
export function isSoftwareRenderer(d: DeviceInfo): boolean {
  return SOFTWARE_GPU.test(d.gpu);
}

/** Starting level before any frame has been measured. */
export function guessQuality(d: DeviceInfo): Quality {
  const fewCores = d.cores <= 4;
  const littleMemory = d.memoryGb !== null && d.memoryGb <= 4;
  if (isSoftwareRenderer(d) || d.mobile || d.cores <= 2 || (d.memoryGb !== null && d.memoryGb <= 2)) return 'low';
  if (DISCRETE_GPU.test(d.gpu)) return 'high';
  if (INTEGRATED_GPU.test(d.gpu)) return fewCores || littleMemory ? 'low' : 'medium';
  return 'medium';
}

/** Reads what the guess needs. Browsers that mask the renderer still expose it through the debug extension. */
export function readDeviceInfo(gl: WebGLRenderingContext | WebGL2RenderingContext): DeviceInfo {
  let gpu = String(gl.getParameter(gl.RENDERER) ?? '');
  if (!gpu || /^webkit webgl$/i.test(gpu)) {
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (ext) gpu = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) ?? gpu);
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    gpu,
    cores: nav.hardwareConcurrency || 4,
    memoryGb: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    mobile: typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
  };
}

export interface AutoQualityState {
  level: Quality;
  /** Highest level this device may try (lowered whenever a level proves too slow). */
  ceiling: Quality;
}

/** What dynamic resolution can still do at the current level. */
export interface ResolutionState {
  /** The pixel ratio is already at the level's minimum. */
  atFloor: boolean;
  /** The pixel ratio is at the level's maximum. */
  atCap: boolean;
}

/** The median frame must be faster than this (30 fps). */
const SLOW_MS = 1000 / 30;
/** A median frame this fast (50 fps) is smooth enough to try a level up. */
const FAST_MS = 1000 / 50;
/** The median is taken over this much play. */
const WINDOW_MS = 3000;
/** Slow for this long (with resolution already at its floor) → one level down. */
const SLOW_HOLD_MS = 4000;
/** Slow for this long → one level down even if resolution could still drop. */
const SLOW_GIVE_UP_MS = 12_000;
/** Smooth for this long → try one level up. */
const FAST_HOLD_MS = 25_000;
/** Ignored after a change or a zone load (shaders compile, textures upload). */
const SETTLE_MS = 3000;
/** A frame longer than this is a pause (tab switch, dialog), unless it keeps happening. */
const HITCH_MS = 1000;
/** Consecutive long frames that count as a slow device rather than a hitch. */
const HITCH_RUN = 3;

const median = (xs: number[]): number => {
  const s = [...xs].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? (s[mid] ?? 0) : ((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2;
};

export class AutoQuality {
  level: Quality;
  ceiling: Quality;
  private samples: { t: number; ms: number }[] = [];
  private slowFor = 0;
  private fastFor = 0;
  private settleUntil = 0;
  private longRun = 0;

  constructor(state: AutoQualityState, now: number) {
    this.level = state.level;
    this.ceiling = rank(state.ceiling) < rank(state.level) ? state.level : state.ceiling;
    this.settle(now);
  }

  get state(): AutoQualityState {
    return { level: this.level, ceiling: this.ceiling };
  }

  /** Starts measuring afresh (after a zone load or a quality change). */
  settle(now: number): void {
    this.samples = [];
    this.slowFor = 0;
    this.fastFor = 0;
    this.longRun = 0;
    this.settleUntil = now + SETTLE_MS;
  }

  /**
   * One rendered frame of play lasting `ms`. Returns the new level when the
   * quality should change.
   */
  frame(ms: number, now: number, resolution: ResolutionState): Quality | null {
    if (ms > HITCH_MS) {
      // One long frame is a hitch; a run of them is a device that is simply this slow.
      this.longRun++;
      if (this.longRun < HITCH_RUN) return null;
      ms = HITCH_MS;
    } else {
      this.longRun = 0;
    }
    if (now < this.settleUntil) return null;
    this.samples.push({ t: now, ms });
    // The window always keeps three frames, so even a device drawing one frame a second is judged.
    while (this.samples.length > 3 && now - (this.samples[0]?.t ?? now) > WINDOW_MS) this.samples.shift();
    // Wait for half a window of data before judging.
    if (this.samples.length < 3 || now - (this.samples[0]?.t ?? now) < WINDOW_MS / 2) return null;
    const m = median(this.samples.map((s) => s.ms));
    if (m > SLOW_MS) {
      this.slowFor += ms;
      this.fastFor = 0;
    } else if (m <= FAST_MS) {
      this.fastFor += ms;
      this.slowFor = 0;
    } else {
      this.slowFor = 0;
      this.fastFor = 0;
    }
    if (this.level !== 'low' && ((this.slowFor >= SLOW_HOLD_MS && resolution.atFloor) || this.slowFor >= SLOW_GIVE_UP_MS)) {
      this.level = lowerQuality(this.level);
      // Whatever proved too slow is off the table for this device.
      this.ceiling = this.level;
      this.settle(now);
      return this.level;
    }
    if (this.fastFor >= FAST_HOLD_MS && resolution.atCap && rank(this.level) < rank(this.ceiling)) {
      this.level = higherQuality(this.level);
      this.settle(now);
      return this.level;
    }
    return null;
  }
}

/** Per-device memory of what Auto settled on (kept out of the account, which moves between machines). */
export const GRAPHICS_PROFILE_KEY = 'aetherfall-online.graphics.v1';

interface StoredProfile extends AutoQualityState {
  gpu: string;
}

const isQuality = (v: unknown): v is Quality => v === 'low' || v === 'medium' || v === 'high';

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** What Auto learned on this device, if it was learned on the same GPU. */
export function loadGraphicsProfile(store: KeyValueStore, gpu: string): AutoQualityState | null {
  try {
    const raw = JSON.parse(store.getItem(GRAPHICS_PROFILE_KEY) ?? 'null') as Partial<StoredProfile> | null;
    if (!raw || raw.gpu !== gpu || !isQuality(raw.level) || !isQuality(raw.ceiling)) return null;
    return { level: raw.level, ceiling: raw.ceiling };
  } catch {
    return null;
  }
}

export function saveGraphicsProfile(store: KeyValueStore, gpu: string, state: AutoQualityState): void {
  try {
    store.setItem(GRAPHICS_PROFILE_KEY, JSON.stringify({ gpu, ...state } satisfies StoredProfile));
  } catch {
    /* storage full or blocked: Auto simply starts from its guess next time */
  }
}

export function clearGraphicsProfile(store: KeyValueStore): void {
  try {
    store.removeItem(GRAPHICS_PROFILE_KEY);
  } catch {
    /* blocked storage */
  }
}
